import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InfoPanel } from "../components/quote/InfoPanel";
import { QuoteEditor } from "../components/quote/QuoteEditor";
import { Footer } from "../components/Footer";
import { NavBar } from "../components/NavBar";
import { useAuthSessionContext } from "../components/routing/auth-session-store";
import { useFetch } from "../hooks/useFetch";
import Loader from "./loader";

type QuoteOrderState = {
	id?: string;
	orderId?: number | null;
	requestId?: number | null;
	estado?: string;
	servicio?: string;
	categoria?: string;
	cliente?: string;
	fecha?: string;
	hora?: string;
	total?: string;
	description?: string;
	urgency?: string;
	ubicacion?: string;
};

type NavigationState = {
	order?: QuoteOrderState;
	providerId?: number | null;
	providerName?: string;
} | null;

type RequestDetailResponse = {
	id?: number;
	type?: string;
	status_request?: string;
	date_request?: string;
	hour_request?: string;
	estimated_date_completion?: string;
	proposed_price_request?: number;
	created_at?: string;
	description_request?: string;
	urgency_request?: string;
	minimun_budget?: number;
	maximun_budget?: number;
	clients?: {
		users?: {
			names?: string;
			last_names?: string;
		};
	};
	addresses?: {
		street_address?: string;
		reference_address?: string;
	};
	services_providers?: {
		name_service?: string;
		categories_services_providers?: Array<{
			categories_providers?: {
				categories_services?: {
					name_category?: string;
				};
			};
		}>;
		categoriesServices_servicesProviders?: Array<{
			categoriesServices?: {
				name_category?: string;
			};
		}>;
	};
	request?: RequestDetailResponse;
	data?: RequestDetailResponse;
};

type OrderDetailResponse = {
	id?: number;
	estimated_date_completion?: string;
	order?: {
		id?: number;
		estimated_date_completion?: string;
	};
	data?: {
		id?: number;
		estimated_date_completion?: string;
		order?: {
			id?: number;
			estimated_date_completion?: string;
		};
	};
};

type StructuredRequestDescription = {
	format?: string;
	category?: string;
	schedule?: {
		date?: string;
		time?: string;
	};
};

const parsePositiveNumber = (value: unknown) => {
	const parsed = Number(value ?? 0);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const formatMoney = (value: number) =>
	`$${value.toLocaleString("es-VE", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

const extractRequestEntity = (
	payload: RequestDetailResponse | null,
): RequestDetailResponse | null => {
	if (!payload || typeof payload !== "object") return null;

	if (typeof payload.id === "number" && payload.id > 0) {
		return payload;
	}

	if (payload.request && typeof payload.request.id === "number") {
		return payload.request;
	}

	if (payload.data && typeof payload.data.id === "number") {
		return payload.data;
	}

	return null;
};

const parseStructuredDescription = (
	raw?: string,
): StructuredRequestDescription | null => {
	if (!raw) return null;
	const marker = "SERVIX_REQUEST_V1::";
	if (!raw.startsWith(marker)) return null;
	const encoded = raw.slice(marker.length).trim();
	if (!encoded) return null;
	try {
		const parsed = JSON.parse(encoded) as StructuredRequestDescription;
		if (parsed?.format !== "SERVIX_REQUEST_V1") return null;
		return parsed;
	} catch {
		return null;
	}
};

const formatRequestDateLabel = (raw?: string) => {
	if (!raw) return undefined;
	const trimmed = raw.trim();
	if (!trimmed) return undefined;

	// Keep the calendar date stable across timezones (mobile/backend may send ISO in UTC).
	const datePrefixMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
	if (datePrefixMatch) {
		const [, year, month, day] = datePrefixMatch;
		return `${day}/${month}/${year}`;
	}

	const parsed = new Date(trimmed);
	if (Number.isNaN(parsed.getTime())) return trimmed;
	return parsed.toLocaleDateString("es-VE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

const formatRequestHourLabel = (raw?: string) => {
	if (!raw) return undefined;
	const trimmed = raw.trim();
	if (!trimmed) return undefined;
	const to12HourLabel = (hour24: number, minutes: number) => {
		const period = hour24 >= 12 ? "PM" : "AM";
		const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
		return `${String(hour12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
	};

	const amPmMatch = /^(\d{1,2}):(\d{2})\s*([ap]m)$/i.exec(trimmed);
	if (amPmMatch) {
		const hour12 = Number(amPmMatch[1]);
		const minutes = Number(amPmMatch[2]);
		const period = amPmMatch[3].toUpperCase();
		if (
			Number.isFinite(hour12) &&
			Number.isFinite(minutes) &&
			hour12 >= 1 &&
			hour12 <= 12 &&
			minutes >= 0 &&
			minutes <= 59
		) {
			const hour24 = period === "PM" ? (hour12 % 12) + 12 : hour12 % 12;
			return to12HourLabel(hour24, minutes);
		}
		return undefined;
	}

	const hour24Match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
	if (hour24Match) {
		const hour = Number(hour24Match[1]);
		const minutes = Number(hour24Match[2]);
		if (
			Number.isFinite(hour) &&
			Number.isFinite(minutes) &&
			hour >= 0 &&
			hour <= 23 &&
			minutes >= 0 &&
			minutes <= 59
		) {
			return to12HourLabel(hour, minutes);
		}
	}

	const parsed = new Date(trimmed);
	if (!Number.isNaN(parsed.getTime())) {
		return to12HourLabel(parsed.getHours(), parsed.getMinutes());
	}

	return undefined;
};

const resolveRequestCategoryName = (request: RequestDetailResponse): string => {
	const structured = parseStructuredDescription(request.description_request);
	if (typeof structured?.category === "string" && structured.category.trim()) {
		return structured.category.trim();
	}

	const currentRelations = Array.isArray(
		request.services_providers?.categories_services_providers,
	)
		? request.services_providers.categories_services_providers
		: [];
	for (const relation of currentRelations) {
		const categoryName =
			relation?.categories_providers?.categories_services?.name_category;
		if (typeof categoryName === "string" && categoryName.trim()) {
			return categoryName.trim();
		}
	}

	const legacyRelations = Array.isArray(
		request.services_providers?.categoriesServices_servicesProviders,
	)
		? request.services_providers.categoriesServices_servicesProviders
		: [];
	for (const relation of legacyRelations) {
		const categoryName = relation?.categoriesServices?.name_category;
		if (typeof categoryName === "string" && categoryName.trim()) {
			return categoryName.trim();
		}
	}

	return "";
};

const mapRequestToOrderState = (request: RequestDetailResponse): QuoteOrderState => {
	const requestId = parsePositiveNumber(request.id);
	const structured = parseStructuredDescription(request.description_request);
	const clientName = [request.clients?.users?.names, request.clients?.users?.last_names]
		.filter((part): part is string => Boolean(part?.trim()))
		.join(" ")
		.trim();
	const categoryName = resolveRequestCategoryName(request);
	const locationParts = [
		request.addresses?.street_address,
		request.addresses?.reference_address,
	]
		.filter((part): part is string => Boolean(part?.trim()))
		.map((part) => part.trim());

	let totalLabel: string | undefined;
	if (
		typeof request.minimun_budget === "number" &&
		typeof request.maximun_budget === "number"
	) {
		totalLabel =
			request.minimun_budget === request.maximun_budget
				? formatMoney(request.minimun_budget)
				: `${formatMoney(request.minimun_budget)} - ${formatMoney(request.maximun_budget)}`;
	} else if (
		typeof request.proposed_price_request === "number" &&
		Number.isFinite(request.proposed_price_request) &&
		request.proposed_price_request > 0
	) {
		totalLabel = formatMoney(request.proposed_price_request);
	}

	return {
		id: requestId ? String(requestId) : undefined,
		requestId: requestId || undefined,
		estado: request.status_request,
		servicio: request.services_providers?.name_service,
		categoria: categoryName || undefined,
		cliente: clientName || "Cliente",
		fecha: formatRequestDateLabel(request.estimated_date_completion),
		hora:
			formatRequestHourLabel(structured?.schedule?.time) ||
			formatRequestHourLabel(request.hour_request),
		total: totalLabel,
		description: request.description_request,
		urgency: request.urgency_request,
		ubicacion: locationParts.join(", ") || undefined,
	};
};

const mergeOrder = (
	stateOrder?: QuoteOrderState,
	fetchedOrder?: QuoteOrderState,
): QuoteOrderState | undefined => {
	if (!stateOrder && !fetchedOrder) return undefined;
	return {
		...(stateOrder ?? {}),
		...(fetchedOrder ?? {}),
	};
};

export function ElaboracionCotizacion() {
	const location = useLocation();
	const navigate = useNavigate();
	const { session } = useAuthSessionContext();
	const { execute: executeGetRequest, loading: isLoading } = useFetch<RequestDetailResponse>({
		endpoint: "/requests/1",
		method: "GET",
		auto: false,
	});
	const { execute: executeGetOrder } = useFetch<OrderDetailResponse>({
		endpoint: "/orders/1",
		method: "GET",
		auto: false,
	});
	const navState = location.state as NavigationState;
	const [fetchedOrder, setFetchedOrder] = useState<QuoteOrderState | undefined>(
		undefined,
	);
	const [preferredDateFromOrder, setPreferredDateFromOrder] = useState<
		string | undefined
	>(undefined);

	const requestIdFromQuery = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return parsePositiveNumber(params.get("requestId"));
	}, [location.search]);

	const providerIdFromQuery = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return parsePositiveNumber(params.get("providerId"));
	}, [location.search]);

	const orderIdFromQuery = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return parsePositiveNumber(params.get("orderId"));
	}, [location.search]);

	const orderIdFromState =
		parsePositiveNumber(navState?.order?.orderId) ||
		parsePositiveNumber(navState?.order?.id);

	const orderIdForPreferredDate = orderIdFromState || orderIdFromQuery;

	const providerId = parsePositiveNumber(navState?.providerId) || providerIdFromQuery;

	useEffect(() => {
		if (!requestIdFromQuery) {
			setFetchedOrder(undefined);
			return;
		}

		let cancelled = false;

		const loadRequest = async () => {
			try {
				const payload = await executeGetRequest({
					endpoint: `/requests/${requestIdFromQuery}`,
					method: "GET",
				});
				if (cancelled) return;
				const request = extractRequestEntity(payload);
				if (!request) return;
				setFetchedOrder(mapRequestToOrderState(request));
			} catch {
				if (!cancelled) setFetchedOrder(undefined);
			}
		};

		void loadRequest();

		return () => {
			cancelled = true;
		};
	}, [requestIdFromQuery, executeGetRequest]);

	useEffect(() => {
		if (!orderIdForPreferredDate) {
			setPreferredDateFromOrder(undefined);
			return;
		}

		let cancelled = false;

		const loadOrderPreferredDate = async () => {
			try {
				const payload = await executeGetOrder({
					endpoint: `/orders/${orderIdForPreferredDate}`,
					method: "GET",
				});
				if (cancelled) return;

				const estimatedDateRaw =
					payload?.estimated_date_completion ||
					payload?.order?.estimated_date_completion ||
					payload?.data?.estimated_date_completion ||
					payload?.data?.order?.estimated_date_completion;

				setPreferredDateFromOrder(formatRequestDateLabel(estimatedDateRaw));
			} catch {
				if (!cancelled) setPreferredDateFromOrder(undefined);
			}
		};

		void loadOrderPreferredDate();

		return () => {
			cancelled = true;
		};
	}, [orderIdForPreferredDate, executeGetOrder]);

	const order = useMemo(
		() => mergeOrder(navState?.order, fetchedOrder),
		[fetchedOrder, navState?.order],
	);
	const preferredDate = preferredDateFromOrder || fetchedOrder?.fecha;
	const effectiveRequestId =
		parsePositiveNumber(order?.requestId) ||
		parsePositiveNumber(order?.id) ||
		requestIdFromQuery;
	const effectiveOrderId =
		parsePositiveNumber(order?.orderId) ||
		(() => {
			const match = /(\d+)$/.exec(String(order?.id ?? ""));
			if (!match) return 0;
			const parsed = Number(match[1]);
			return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
		})() ||
		orderIdFromQuery;
	const hasRequiredIds =
		effectiveRequestId > 0 && providerId > 0 && effectiveOrderId > 0;
	const normalizedOrderStatus = String(order?.estado ?? "").toLowerCase();
	const isOrderCompleted =
		normalizedOrderStatus.includes("complet") ||
		normalizedOrderStatus.includes("pagad");

	if (isLoading) {
		return (
			<div className="min-h-screen w-full flex items-center justify-center bg-(--bg)">
				<Loader />
			</div>
		);
	}

	return (
		<div className='min-h-screen w-full bg-(--bg)'>
			<NavBar isLogged={Boolean(session?.user?.id)} />
			<div className='mx-auto w-full max-w-6xl px-3 py-5 sm:px-6 sm:py-6 lg:px-8'>
				<button
					type='button'
					onClick={() => {
						if (window.history.length > 1) {
							navigate(-1);
							return;
						}
						navigate("/service/profile");
					}}
					className='mb-3 mt-2 inline-flex cursor-pointer items-center text-sm font-semibold text-slate-700 transition hover:text-primary sm:mb-4 sm:mt-3'
				>
					&lt; Volver al perfil de servicios
				</button>
				<div className='mb-5 sm:mb-6'>
					<h1 className='mt-3 text-xl font-bold text-text-secondary sm:mt-6 sm:text-2xl'>
						Elaborar Cotización
					</h1>
					<span className='mt-1 block text-sm text-text-tertiary'>
						Crea una cotización detallada para la solicitud del cliente
					</span>
					{!hasRequiredIds ? (
						<span className='mt-2 block text-sm font-medium text-red-600'>
							No se pudo identificar solicitud, proveedor o pedido para
							cotizar.
						</span>
					) : null}
				</div>
				<div className='grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8'>
					<InfoPanel
						requestId={
							effectiveRequestId ? String(effectiveRequestId) : undefined
						}
						clientName={order?.cliente}
						urgency={order?.urgency}
						description={order?.description}
						date={preferredDate}
						hour={order?.hora}
						location={order?.ubicacion}
					/>
					<QuoteEditor
						categoryName={order?.categoria}
						initialServiceName={order?.servicio}
						requestId={effectiveRequestId}
						orderId={effectiveOrderId}
						providerId={providerId}
						isQuotationLocked={isOrderCompleted}
					/>
				</div>
			</div>
			<Footer />
		</div>
	);
}
